import { SidebarProvider } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { useCrmAccess } from "@/hooks/use-crm-access";
import { Navigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Search, Shield, UserCog, RefreshCw, Phone, MapPin, Mail, Edit, MoreVertical, Trash2, Users } from "lucide-react";
import { useStaff, useSyncSuperusersToStaff, useDeleteStaff } from "@/hooks/use-staff";
import { StaffModal } from "@/components/crm/StaffModal";
import { StaffDetailModal } from "@/components/crm/StaffDetailModal";
import { StaffEditModal } from "@/components/crm/StaffEditModal";
import { StaffAccountStatus } from "@/components/crm/StaffAccountStatus";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useState } from "react";
import { FixedPageHeader } from "@/components/ui/fixed-page-header";
import { PAGE_HEADERS } from "@/constants/page-headers";

const CrmStaff = () => {
  const { data: hasCrmAccess, isLoading } = useCrmAccess();
  const { data: staff = [], isLoading: staffLoading } = useStaff();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStaffId, setSelectedStaffId] = useState<string | null>(null);
  const [editStaff, setEditStaff] = useState<any>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const syncSuperusers = useSyncSuperusersToStaff();
  const deleteStaff = useDeleteStaff();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-slate-600">Loading...</div>
      </div>
    );
  }

  if (!hasCrmAccess) {
    return <Navigate to="/dashboard" replace />;
  }

  const filteredStaff = staff.filter(member =>
    `${member.first_name} ${member.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.role.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (member.title && member.title.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'superuser':
        return 'bg-red-100 text-red-800';
      case 'admin':
        return 'bg-orange-100 text-orange-800';
      case 'manager':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'superuser':
      case 'admin':
        return <Shield className="h-4 w-4" />;
      case 'manager':
        return <UserCog className="h-4 w-4" />;
      default:
        return <Users className="h-4 w-4" />;
    }
  };

  const handleEditStaff = (member: any) => {
    setEditStaff(member);
    setEditModalOpen(true);
  };

  const handleViewStaff = (member: any) => {
    setSelectedStaffId(member.id);
    setDetailModalOpen(true);
  };

  const handleDeleteStaff = async (staffId: string) => {
    try {
      await deleteStaff.mutateAsync(staffId);
    } catch (error) {
      console.error("Error deleting staff:", error);
    }
  };

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  if (staffLoading) {
    return (
      <SidebarProvider>
        <div className="flex min-h-screen w-full bg-background">
          <AppSidebar />
          <main className="flex-1">
            <div className="p-8">
              <div className="text-slate-600">Loading staff...</div>
            </div>
          </main>
        </div>
      </SidebarProvider>
    );
  }

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full bg-background">
        <AppSidebar />
        <main className="flex-1">
          <FixedPageHeader 
            {...PAGE_HEADERS.crmStaff} 
            actions={
              <div className="flex gap-3">
                <Button
                  onClick={() => syncSuperusers.mutate()}
                  disabled={syncSuperusers.isPending}
                  variant="outline"
                  size="sm"
                  className="gap-2 text-xs"
                >
                  <RefreshCw className="h-3 w-3" />
                  {syncSuperusers.isPending ? "Syncing..." : "Sync Superusers"}
                </Button>
                <StaffModal />
              </div>
            } 
          />
          <div className="px-4 md:px-8 py-6 md:py-8 space-y-8" style={{ paddingTop: "8.5rem" }}>

            {/* Summary Card */}
            <div className="grid grid-cols-1 md:grid-cols-1 gap-6 max-w-md">
              <Card className="bg-white border border-black rounded-xl shadow-sm">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-semibold text-slate-700">Total Staff</CardTitle>
                    <div className="bg-slate-100 p-2 rounded-lg">
                      <Users className="h-5 w-5 text-slate-600" />
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-slate-800">{staff.length}</div>
                </CardContent>
              </Card>
            </div>

            {/* Search */}
            <div className="flex items-center gap-4">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-500 h-4 w-4" />
                <Input
                  placeholder="Search staff members..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 border-slate-300 focus:border-teal-500 focus:ring-teal-500 rounded-lg bg-white"
                />
              </div>
            </div>

            {/* Staff Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredStaff.map((member) => (
                <Card 
                  key={member.id} 
                  className="group hover:shadow-lg transition-all duration-300 overflow-hidden hover:-translate-y-1 cursor-pointer bg-white"
                  onClick={() => handleViewStaff(member)}
                >
                  {/* Header without teal gradient */}
                  <div className="h-1 bg-gradient-to-r from-slate-200 to-slate-300 shadow-sm"></div>
                  <CardHeader className="pb-4 bg-white">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3 flex-1">
                        <Avatar className="w-14 h-14 border shadow-sm ring-1" style={{ borderColor: "black", boxShadow: "0 0 0 1px black" }}>
                          <AvatarImage 
                            src={member.profile_image_url} 
                            alt={`${member.first_name} ${member.last_name}`} 
                          />
                          <AvatarFallback className="bg-white text-black font-bold text-lg">
                            {getInitials(member.first_name, member.last_name)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <h3 className="font-bold text-lg text-slate-900 group-hover:text-slate-700 transition-colors duration-200 truncate">
                            {member.first_name} {member.last_name}
                          </h3>
                          {member.title && (
                            <p className="text-sm text-slate-600">{member.title}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge 
                          className="text-xs font-semibold px-3 py-1 rounded-full shadow-sm text-black"
                          style={{ backgroundColor: "white" }}
                        >
                          {getRoleIcon(member.permissions || member.role)}
                          {member.permissions || member.role}
                        </Badge>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <div className="flex items-center gap-3 text-sm text-slate-700 hover:text-slate-900 transition-colors duration-150 group/item">
                        <div className="flex items-center justify-center h-8 w-8 bg-slate-50 rounded-lg group-hover/item:bg-slate-100 transition-colors duration-150">
                          <Mail className="h-4 w-4 text-black" />
                        </div>
                        <span className="truncate font-medium">{member.email}</span>
                      </div>
                      {member.phone && (
                        <div className="flex items-center gap-3 text-sm text-slate-700 hover:text-slate-900 transition-colors duration-150 group/item">
                          <div className="flex items-center justify-center h-8 w-8 bg-slate-50 rounded-lg group-hover/item:bg-slate-100 transition-colors duration-150">
                            <Phone className="h-4 w-4 text-black" />
                          </div>
                          <span className="font-medium">{member.phone}</span>
                        </div>
                      )}
                      {member.address && (
                        <div className="flex items-center gap-3 text-sm text-slate-700 hover:text-slate-900 transition-colors duration-150 group/item">
                          <div className="flex items-center justify-center h-8 w-8 bg-slate-50 rounded-lg group-hover/item:bg-slate-100 transition-colors duration-150">
                            <MapPin className="h-4 w-4 text-black" />
                          </div>
                          <span className="truncate font-medium">{member.address}</span>
                        </div>
                      )}
                        <div className="text-xs text-slate-500 pt-2">
                          <p>Created: {new Date(member.created_at).toLocaleDateString()}</p>
                          <div className="mt-2">
                            <StaffAccountStatus staff={member} size="sm" />
                          </div>
                        </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* No results message */}
            {filteredStaff.length === 0 && (
              <Card className="bg-white border-slate-200 rounded-xl shadow-sm">
                <CardContent className="p-8 text-center">
                  <Users className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-800 mb-2">No Staff Members Found</h3>
                  <p className="text-slate-600 mb-4">
                    {searchTerm ? "No staff members match your search criteria." : "Get started by adding your first staff member."}
                  </p>
                  {!searchTerm && <StaffModal />}
                </CardContent>
              </Card>
            )}

            {/* Staff Detail Modal */}
            {selectedStaffId && detailModalOpen && (
              <StaffDetailModal
                staffId={selectedStaffId}
                open={detailModalOpen}
                onOpenChange={(open) => {
                  setDetailModalOpen(open);
                  if (!open) setSelectedStaffId(null);
                }}
                onEdit={() => {
                  const staffMember = staff.find(s => s.id === selectedStaffId);
                  if (staffMember) {
                    setDetailModalOpen(false);
                    handleEditStaff(staffMember);
                  }
                }}
              />
            )}

            {/* Staff Edit Modal */}
            {editStaff && (
              <StaffEditModal
                staff={editStaff}
                open={editModalOpen}
                onOpenChange={(open) => {
                  setEditModalOpen(open);
                  if (!open) setEditStaff(null);
                }}
              />
            )}
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
};

export default CrmStaff;
